import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, Output, signal, ViewChild } from '@angular/core';
import { currentTheme, getTheme } from '@shared/utils/theme.util';
import { EditorComponent } from '@tinymce/tinymce-angular';
import tinymce, { Editor, EditorEvent } from 'tinymce';
import { FrameEditorConstants } from '../../constants/frame-editor.constants';
import { stopEvent } from '@shared/utils/elements-util';
import { EditorContextMenuOptions, FrameEditorService } from '../../services/frame-editor.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { keyCodes } from '@local/ts-infra';
import { CollectionsService } from '../../../../services/collections.service';
import { FrameEditorContextMenuService } from '../../services/frame-editor-context-menu.service';
import '../../constants/frame-editor-icons';
import { FrameEditorFileService } from '../../services/frame-editor-file.service';

@UntilDestroy()
@Component({
  selector: 'readonly-frame-editor',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BaseFrameEditorComponent {
  private readonly MOUSE_LEFT_CLICK_CODE = 0;
  private readonly HEIGHT_TOOLBAR = 48;
  private readonly HEIGHT_SLIDING_TOOLBAR = 38;
  private _id: string;

  protected frameEditorContextMenuService = inject(FrameEditorContextMenuService);
  protected frameEditorService = inject(FrameEditorService);
  protected frameEditorFileService = inject(FrameEditorFileService);
  protected collectionsService = inject(CollectionsService);

  protected abstract editor: EditorComponent;
  protected abstract editorContainer: ElementRef;
  protected abstract readonlyFrame: boolean;

  editorInit: any;
  visibleEditor = signal(false);
  initial = signal(false);

  @Input() html: string;
  @Input() telemetryLocation: string;
  @Input() followingId: string;
  @Input() bodyClass: string;
  @Input() collectionId: string;
  @Input() set id(value: string) {
    this._id = value;
    this.setUpEditor();
    this.addSubscription();
  }

  @Output() onInit = new EventEmitter();
  @Output() onHtmlChange = new EventEmitter();
  @Output() closeDropdownTags = new EventEmitter();
  @Output() closePopup = new EventEmitter();

  get id(): string {
    return this._id;
  }

  protected abstract setEditorInit();

  protected abstract setSetupEditorInit(editor: Editor);

  protected abstract handleKeys(event: EditorEvent<KeyboardEvent>);

  setUpEditor() {
    this.editorInit = this.setEditorInit();

    const theme = getTheme(currentTheme());
    if (theme === 'dark') {
      if (!this.editorInit.skin.includes('dark')) {
        this.editorInit.skin += '-' + theme;
      }
    } else {
      this.editorInit.skin = this.editorInit.skin.replace('-dark', '');
    }
    this.initial.set(true);
  }

  handleInitEditor(editor: Editor) {
    editor.contentDocument.documentElement.style.cssText = document.documentElement.style.cssText;
    editor.contentDocument.body.style.cssText = document.body.style.cssText;

    editor.contentDocument.addEventListener('click', (e) => {
      const tagsDropdown = document.getElementsByClassName('p-overlay-content');
      if (tagsDropdown.length) {
        this.closeDropdownTags.emit();
      }
      this.clickAttachment(e);
    });
    editor.contentDocument.addEventListener('auxclick', (e) => {
      this.handleRightClick(e);
    });
    editor.contentDocument.addEventListener('contextmenu', (e) => {
      this.handleRightClick(e);
    });
    editor.contentDocument.addEventListener(
      'dblclick',
      (e) => {
        this.handleDblClick(e);
      },
      true
    );

    this.visibleEditor.set(true);
  }

  private addSubscription() {
    this.frameEditorContextMenuService.saveEditor$.pipe(untilDestroyed(this)).subscribe(() => {
      this.editor?.editor?.save();
    });
  }

  addLinkListenersToEditor(editor: Editor) {
    editor.on('mousedown', (e) => {
      const href = e.target.href || e.target.parentElement?.href;
      if (href && e.button === this.MOUSE_LEFT_CLICK_CODE && (e.target?.tagName.toLocaleLowerCase() !== 'img' || this.readonlyFrame)) {
        //Do not allow opening a link by clicking on an image in edit mode (only by right-clicking)
        window.open(href);
      }
    });
  }

  clickAttachment(event) {
    const attachment = this.frameEditorFileService.getAttachment(event);
    if (!attachment) return;
    if (event.target.className?.includes(FrameEditorConstants.CLASS_DELETE_ATTACHMENT)) {
      attachment.remove();
      this.frameEditorContextMenuService.sendEvent('context_menu.open', this.telemetryLocation, { label: 'delete' });
      this.frameEditorContextMenuService.sendEvent('results.action', this.telemetryLocation, { label: 'delete' });
      this.editor?.editor?.save();
    }
    if (event.target.className?.includes(FrameEditorConstants.CLASS_CONTEXT_MENU_ATTACHMENT)) {
      this.openContextMenu(event, { data: attachment, type: 'attachment' });
    }
  }

  handleRightClick(event: MouseEvent) {
    const selectedText = tinymce.get(this.id)?.selection.getContent({ format: 'text' });
    let selectedElement = tinymce.get(this.id).selection.getNode();
    if (selectedElement.tagName.toLocaleLowerCase() === 'span' && selectedElement.querySelector('video')) {
      const id = selectedElement.id || selectedElement.getAttribute('data-mce-p-id'); // tinymce put the id in this attribute
      this.openContextMenu(event, { data: { selectedElement, editor: tinymce.get(this.id), id }, type: 'video' });
    } else if (selectedElement.tagName.toLocaleLowerCase() === 'hr' || event.target?.['tagName'].toLocaleLowerCase() === 'hr') {
      selectedElement = event.target?.['tagName'].toLocaleLowerCase() === 'hr' ? (event.target as HTMLElement) : selectedElement;
      this.openContextMenu(event, { data: { selectedElement, editor: tinymce.get(this.id) }, type: 'hr' });
    } else if (['a'].includes(selectedElement.tagName.toLocaleLowerCase())) {
      if (this.readonlyFrame) {
        stopEvent(event);
        return;
      }
      this.openContextMenu(event, { data: { selectedElement, editor: tinymce.get(this.id) }, type: 'a' });
    } else if (['td', 'tr'].includes(selectedElement.tagName.toLocaleLowerCase())) {
      if (this.readonlyFrame) {
        event.preventDefault();
        return;
      }
    } else if (selectedElement.tagName.toLocaleLowerCase() === 'pre' || (selectedText && !(event?.target as any)?.closest('.attachment'))) {
      this.openContextMenu(event, { data: { selectedElement, editor: tinymce.get(this.id) }, type: 'code' });
    } else {
      this.auxclickAttachment(event);
    }
  }

  handleDblClick(event: any) {
    const isCodeBlock = !!event?.target?.closest('pre');
    if (this.readonlyFrame && isCodeBlock) {
      stopEvent(event);
    }
  }

  auxclickAttachment(event: MouseEvent) {
    const attachment = this.frameEditorFileService.getAttachment(event);
    if (!attachment) return;
    this.openContextMenu(event, { data: attachment, type: 'attachment' });
  }

  openContextMenu(event: MouseEvent, contextMenuOp: { type: EditorContextMenuOptions; data: any }) {
    event?.preventDefault();
    this.frameEditorContextMenuService.sendEvent('context_menu.open', this.telemetryLocation, { label: 'open_menu' });
    const editorPosition = this.editorContainer?.nativeElement?.getBoundingClientRect() || { x: 0, y: 0 };
    const toolbarOpen = document.getElementsByClassName('tox-toolbar__overflow--open');
    let positionWithToolbar = 0;
    if (!this.readonlyFrame) {
      positionWithToolbar += this.HEIGHT_TOOLBAR;
      if (toolbarOpen.length) {
        positionWithToolbar += this.HEIGHT_SLIDING_TOOLBAR;
      }
    }
    const x = event.x + editorPosition.x;
    const y = event.y + editorPosition.y + positionWithToolbar;
    let contextMenu;
    switch (contextMenuOp.type) {
      case 'attachment':
        contextMenu = this.frameEditorContextMenuService.openAttachmentContextMenu(
          { x, y },
          contextMenuOp.data,
          this.collectionId,
          !this.readonlyFrame,
          this.telemetryLocation,
          { x: editorPosition.x, y: editorPosition.y + positionWithToolbar },
          this.followingId,
          this.id
        );
        break;
      default:
        contextMenu = this.frameEditorContextMenuService.openContextMenu(
          contextMenuOp.type,
          this.id,
          { x, y },
          contextMenuOp.data,
          this.telemetryLocation,
          this.readonlyFrame
        );
        break;
    }
    event?.stopPropagation();
    if (this.editor) {
      const isDisabled = this.editor.disabled;
      this.editor.disabled = true;
      contextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
        if (!isDisabled) {
          this.editor.disabled = false;
        }
      });
    }
  }

  addKeyboardToEditor(editor: Editor) {
    editor.on('keydown', (e) => {
      if (e.code === keyCodes.escape) {
        stopEvent(e);
        this.closePopup.emit();
      }
      this.handleKeys(e);
    });
  }

  changeFocus(focus: string) {
    this.collectionsService.onFocusChange = focus;
  }
}
